import { Badge, Card, MUTED_TEXT, Spinner, stackStyle } from "@dayvise/react-ui";
import { useQuery } from "@tanstack/react-query";
import { useDayviseClient } from "./providers.tsx";

/**
 * Codex runtime and ChatGPT-plan connection status (CMD-096, PAUTH-09).
 *
 * The connection half is real: it shows the hosted provider-identity status
 * from the live API. The optional Codex runtime adapter is not built yet — the
 * card says so plainly instead of implying a runtime is selectable. Hosted
 * plan usage needs Dayvise's own client registration (PAUTH-01); until then
 * the card points at BYO API keys, which remain a separate explicit choice.
 */
export function CodexRuntimeCard() {
  const client = useDayviseClient();
  const status = useQuery({
    queryKey: ["providerIdentity", "openai"] as const,
    queryFn: () => client.providerIdentities.status("openai"),
  });

  return (
    <Card
      title="Codex runtime & ChatGPT-plan connection"
      subtitle="Connection status is live. The optional Codex execution runtime adapter is pending its own spike; BYO API keys remain the separate, always-available choice."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {status.isPending ? <Spinner label="Reading connection status" /> : null}
        {status.error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{status.error instanceof Error ? status.error.message : "Could not read the connection status"}</p> : null}
        {status.data ? (
          <>
            <div style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center", flexWrap: "wrap" }}>
              <Badge tone={status.data.planFundedAvailable ? "success" : status.data.configured ? "warning" : "neutral"}>
                {status.data.planFundedAvailable ? "Plan connection live" : status.data.configured ? status.data.status === "linked" ? "Identity linked; plan access pending" : "Registration configured, not linked" : "Hosted registration pending"}
              </Badge>
              {status.data.status === "linked" ? <Badge tone="neutral">identity linked</Badge> : null}
              {status.data.planKind ? <Badge tone="neutral">{status.data.planKind}</Badge> : null}
            </div>
            <p style={{ ...MUTED_TEXT, margin: 0 }}>{status.data.reason}</p>
            <p style={{ ...MUTED_TEXT, margin: 0 }}>
              Codex runtime adapter: <strong>pending</strong> — tool mediation, durable pause/resume and the tenant boundary it must use are
              already real; the adapter that speaks the Codex protocol is the remaining spike.
            </p>
          </>
        ) : null}
      </div>
    </Card>
  );
}
